/**
 * @author Erik Haßlmeyer <erik.hasslmeyer@refinio.net>
 * @copyright REFINIO GmbH 2022
 * @license CC-BY-NC-SA-2.5; portions MIT License
 * @version 0.0.1
 */
import { ensureSecretKey } from '../crypto/encryption.js';
import { ensureSecretSignKey } from '../crypto/sign.js';
import { STORAGE } from '../storage-base-common.js';
import { exists, readPrivateBinaryRaw, writePrivateBinaryRaw } from '../system/storage-base.js';
/**
 * Filename for a secret encryption key of a person.
 *
 * @param {SHA256Hash<Keys>} keysObjHash
 * @returns {string}
 */
function filenameForEncryptionKey(keysObjHash) {
    return `${keysObjHash}.encrypt`;
}
/**
 * Filename for a secret sign key of a person.
 *
 * @param {SHA256Hash<Keys>} keysObjHash
 * @returns {string}
 */
function filenameForSignKey(keysObjHash) {
    return `${keysObjHash}.sign`;
}
/**
 * Stores the secret keys encrypted with the master key in the private section of the OneDB.
 *
 * @param {SHA256Hash<Keys>} keysObjHash
 * @param {SecretKey} secretEncryptionKey
 * @param {SecretSignKey} secretSignKey
 * @param {MasterKeyManager} masterKeyManager
 * @returns {Promise<void>}
 */
export async function storeSecretKeys(keysObjHash, secretEncryptionKey, secretSignKey, masterKeyManager) {
    await Promise.all([
        writePrivateBinaryRaw(filenameForEncryptionKey(keysObjHash), masterKeyManager.encryptDataWithMasterKey(secretEncryptionKey).buffer),
        writePrivateBinaryRaw(filenameForSignKey(keysObjHash), masterKeyManager.encryptDataWithMasterKey(secretSignKey).buffer)
    ]);
}
/**
 * Get the secret keys belonging to a Keys object.
 *
 * @param {SHA256Hash<Keys>} keysObjHash
 * @param {MasterKeyManager} masterKeyManager
 * @returns {Promise<{secretEncryptionKey: SecretKey, secretSignKey: SecretSignKey}>}
 */
export async function getSecretKeys(keysObjHash, masterKeyManager) {
    const [encKey, signKey] = await Promise.all([
        await readPrivateBinaryRaw(filenameForEncryptionKey(keysObjHash)),
        await readPrivateBinaryRaw(filenameForSignKey(keysObjHash))
    ]);
    return {
        secretEncryptionKey: ensureSecretKey(masterKeyManager.decryptDataWithMasterKey(new Uint8Array(encKey))),
        secretSignKey: ensureSecretSignKey(masterKeyManager.decryptDataWithMasterKey(new Uint8Array(signKey)))
    };
}
/**
 * Check whether we have secret keys for the passed keys object.
 *
 * @param {SHA256Hash<Keys>} keysObjHash
 * @returns {Promise<boolean>}
 */
export async function hasSecretKeys(keysObjHash) {
    const [hasEncryptionKeyFile, hasSignKeyFile] = await Promise.all([
        exists(filenameForEncryptionKey(keysObjHash), STORAGE.PRIVATE),
        exists(filenameForSignKey(keysObjHash), STORAGE.PRIVATE)
    ]);
    return hasEncryptionKeyFile && hasSignKeyFile;
}
//# sourceMappingURL=key-storage-secret.js.map